import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
  type ReactNode,
} from 'react';
import { Icon } from '@iconify/react';
import {
  Card,
  CardBody,
  Chip,
  Divider,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
  Textarea,
} from '@nextui-org/react';
import CtyPromptInput, {
  checkType,
  CtyButton,
  CtySpinner,
  CtyShowToast,
  promptInputType,
  JsonMapperMonacoEditor,
} from '@circuitry-ai/doc-ui';
import {
  useGenerateMapperMutation,
  type GenerateMapperResponseData,
  type MapperField,
  type MapperDefinition,
  type MapperItem,
  type MapperMapping,
  useApproveMapperMutation,
  useUpdateMapperMutation,
} from '@circuitry-ai/doc-data';
import { useTranslation } from 'react-i18next';

interface SchemaMapperMapperProps {
  readonly mapper: MapperItem | null;
  readonly onMapperChange?: (nextMapper: MapperItem | null) => void;
  readonly isMapperDataLoading?: boolean;
}

interface JsonUploadPanelProps {
  readonly title: string;
  readonly textValue: string;
  readonly fileName: string;
  readonly onTextChange: (value: string) => void;
  readonly onFileSelect: (file: File) => Promise<void>;
  readonly panelType: 'source' | 'target';
  readonly isCollapsible?: boolean;
  readonly isCollapsed?: boolean;
  readonly onToggleCollapse?: () => void;
  readonly side?: 'left' | 'right';
}

interface FieldListPanelProps {
  readonly title: string;
  readonly subtitle: string;
  readonly fields: MapperField[];
  readonly searchValue: string;
  readonly onSearchChange: (value: string) => void;
  readonly isCollapsible?: boolean;
  readonly isCollapsed?: boolean;
  readonly onToggleCollapse?: () => void;
  readonly side?: 'left' | 'right';
}

type NormalizedMapperMapping = Omit<MapperMapping, 'id' | 'source' | 'destination'> & {
  id: string;
  source: string;
  destination: string;
};

interface MappingRowItem {
  id: string;
  source: string;
  destination: string;
  sourceType: string;
  destinationType: string;
  confidence: number;
  transform: 'rename' | 'sum' | 'concat' | 'lookup' | 'date_format' | 'custom';
  accepted: boolean;
  rejected: boolean;
  override: boolean;
  sourceOverridden?: boolean;
  destinationOverridden?: boolean;
}

interface EditingState {
  rowId: string;
  field: 'source' | 'destination';
  value: string;
  originalValue: string;
}

type ApprovalAction = 'approve' | 'reject';

function getCollapsePanelIcon(side: 'left' | 'right', isCollapsed: boolean): string {
  if (side === 'left') {
    if (isCollapsed) return 'material-symbols:keyboard-double-arrow-right-rounded'    
    return 'material-symbols:keyboard-double-arrow-left-rounded';
  }
  if (isCollapsed) return 'material-symbols:keyboard-double-arrow-left-rounded'
  return 'material-symbols:keyboard-double-arrow-right-rounded'
}

function getMapperGeneratedData(mapper: MapperItem | null): GenerateMapperResponseData | null {
  if (!mapper?.mapper_definition?.mappings?.length) return null;

  return {
    source_fields: mapper.source_fields ?? [],
    target_fields: mapper.target_fields ?? [],
    mapper_definition: mapper.mapper_definition,
    mapper_status: mapper.mapper_status ?? mapper.mapper_definition.status,
    mapper_version: mapper.mapper_version ?? mapper.mapper_definition.version,
  };
}

function buildUpdatedMapperDefinition(
  existingDefinition: MapperDefinition | undefined,
  rows: MappingRowItem[]
): MapperDefinition {
  const existingMappings = Array.isArray(existingDefinition?.mappings)
    ? existingDefinition.mappings
    : [];
  const existingMappingMap = new Map(
    existingMappings.map((mapping) => [mapping.id ?? '', mapping])
  );

  return {
    ...existingDefinition,
    mappings: rows.map((row) => {
      const currentMapping = existingMappingMap.get(row.id);
      const isApproved = row.accepted && !row.rejected;
      const isRejected = row.rejected;
      let mappingStatus: 'rejected' | 'approved' | 'pending';
      if (isRejected) {
        mappingStatus = 'rejected';
      } else if (isApproved) {
        mappingStatus = 'approved';
      } else {
        mappingStatus = 'pending';
      }

      return {
        ...currentMapping,
        id: row.id,
        source: row.source,
        destination: row.destination,
        approval: isApproved,
        rejected: isRejected,
        status: mappingStatus,
      };
    }),
  };
}

function stringifyMapperDefinitionJson(
  definition: MapperDefinition | undefined,
  rows?: MappingRowItem[]
): string {
  const payload = rows
    ? buildUpdatedMapperDefinition(definition, rows)
    : definition ?? {};
  return JSON.stringify(payload, null, 2);
}

function getNormalizedGeneratedData(
  mapperData: MapperItem | null | undefined,
  fallbackData: GenerateMapperResponseData | null
): GenerateMapperResponseData | null {
  const normalizedData = getMapperGeneratedData(mapperData ?? null);
  if (!normalizedData) return fallbackData;

  return {
    ...normalizedData,
    source_fields: normalizedData.source_fields?.length
      ? normalizedData.source_fields
      : fallbackData?.source_fields ?? [],
    target_fields: normalizedData.target_fields?.length
      ? normalizedData.target_fields
      : fallbackData?.target_fields ?? [],
  };
}

/** Merge server mapper_definition with the payload we just saved so mappings never regress to stale UI. */
function mergePersistedMapperDefinition(
  fromServer: MapperDefinition | undefined,
  persisted: MapperDefinition
): MapperDefinition {
  return {
    ...(fromServer),
    ...persisted,
  };
}

function omitUndefinedMapperItemFields(
  updates: Partial<MapperItem> | undefined
): Partial<MapperItem> {
  if (!updates) return {};
  return Object.fromEntries(
    Object.entries(updates).filter(([, value]) => value !== undefined)
  ) as Partial<MapperItem>;
}

const JSON_FILE_SIZE_LIMIT = 5 * 1024 * 1024;
const SAMPLE_JSON_PLACEHOLDER = `{
  "id": 123
}`;
const DEFAULT_TRANSFORMS: MappingRowItem['transform'][] = [
  'rename',
  'rename',
  'concat',
  'date_format',
  'sum',
  'rename',
  'rename',
  'rename',
  'lookup',
  'date_format',
];

function safeParseJson(value: string): Record<string, unknown> | null {
  try {
    const parsed = JSON.parse(value);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
    return null;
  } catch {
    return null;
  }
}

function parsePathSegments(path: string): Array<string | number> {
  if (!path) return [];

  const segments: Array<string | number> = [];
  const dotParts = path.split('.');

  dotParts.forEach((part) => {
    if (!part) return;
    const regex = /([^[\]]+)|\[(\d+)\]/g;
    let match: RegExpExecArray | null = regex.exec(part);
    while (match) {
      if (match[1]) segments.push(match[1]);
      if (match[2]) segments.push(Number(match[2]));
      match = regex.exec(part);
    }
  });

  return segments;
}

function buildSampleFromFields(fields: MapperField[] | undefined): Record<string, unknown> | null {
  if (!Array.isArray(fields) || fields.length === 0) return null;

  const output: Record<string, unknown> = {};

  fields.forEach((field) => {
    const path = String(field.path ?? '').trim();
    if (!path) return;

    const segments = parsePathSegments(path);
    if (segments.length === 0) return;

    let current: unknown = output;

    for (let index = 0; index < segments.length; index += 1) {
      const segment = segments[index];
      const nextSegment = segments[index + 1];
      const isLastSegment = index === segments.length - 1;
      const shouldCreateArray = typeof nextSegment === 'number';

      if (typeof segment === 'number') {
        if (!Array.isArray(current)) return;
        const currentArray = current as unknown[];
        if (isLastSegment) {
          currentArray[segment] = field.sample_value ?? null;
          return;
        }

        if (
          currentArray[segment] === undefined ||
          currentArray[segment] === null ||
          typeof currentArray[segment] !== 'object'
        ) {
          currentArray[segment] = shouldCreateArray ? [] : {};
        }
        current = currentArray[segment];
        continue;
      }

      if (!current || typeof current !== 'object' || Array.isArray(current)) return;
      const currentRecord = current as Record<string, unknown>;

      if (isLastSegment) {
        currentRecord[segment] = field.sample_value ?? null;
        return;
      }

      if (
        currentRecord[segment] === undefined ||
        currentRecord[segment] === null ||
        typeof currentRecord[segment] !== 'object'
      ) {
        currentRecord[segment] = shouldCreateArray ? [] : {};
      }

      current = currentRecord[segment];
    }
  });

  return Object.keys(output).length > 0 ? output : null;
}

let mappingIdCounter = 0;

function createMappingId() {
  const timePart = Date.now().toString(36);

  if (typeof globalThis !== 'undefined' && globalThis.crypto) {
    if (typeof globalThis.crypto.randomUUID === 'function') {
      return `mapping-${globalThis.crypto.randomUUID()}`;
    }

    if (typeof globalThis.crypto.getRandomValues === 'function') {
      const bytes = new Uint8Array(8);
      globalThis.crypto.getRandomValues(bytes);
      const randomPart = Array.from(bytes, (value) =>
        value.toString(16).padStart(2, '0')
      ).join('');
      return `mapping-${timePart}-${randomPart}`;
    }
  }

  // Fallback for rare non-crypto environments.
  mappingIdCounter += 1;
  return `mapping-${timePart}-${mappingIdCounter.toString(36)}`;
}

function normalizeMappingValue(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return '';
}

function normalizeMappings(mappings: MapperMapping[] | undefined): NormalizedMapperMapping[] {
  if (!Array.isArray(mappings)) return [];
  return mappings.map((mapping, index) => {
    const id = mapping.id ?? `${createMappingId()}-${index}`;
    const source = normalizeMappingValue(mapping.source);
    const destination = normalizeMappingValue(mapping.destination);
    return { ...mapping, id, source, destination };
  });
}

function readJsonFile(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
}

function formatSampleValue(sampleValue: unknown) {
  if (sampleValue === null) return 'null';
  if (sampleValue === undefined) return '-';
  if (typeof sampleValue === 'string') return sampleValue;
  if (typeof sampleValue === 'number' || typeof sampleValue === 'boolean') {
    return String(sampleValue);
  }

  try {
    return JSON.stringify(sampleValue);
  } catch {
    return '-';
  }
}

function truncateText(value: string, limit = 80) {
  if (value.length <= limit) return value;
  return `${value.slice(0, limit)}...`;
}

function numberFromApi(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return 0;
}

function formatPercentageForDisplay(value: unknown): string {
  const n = numberFromApi(value);
  return Number(n).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function mappingConfidenceLabel(confidence: number) {
  return `${Math.max(0, Math.round(confidence))}%`;
}

function getPanelCardClassName() {
  return 'flex h-full min-h-0 flex-col overflow-hidden rounded-[16px] border border-[#d9e2ec] bg-white shadow-[0_2px_6px_rgba(15,23,42,0.04)] dark:border-default-200 dark:bg-content1 dark:shadow-none';
}

function normalizeFieldType(value?: string) {
  return String(value ?? 'STRING').toUpperCase();
}

function parseBooleanLike(value: unknown) {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const normalizedValue = value.trim().toLowerCase();
    if (normalizedValue === 'true' || normalizedValue === '1' || normalizedValue === 'yes') {
      return true;
    }
    if (normalizedValue === 'false' || normalizedValue === '0' || normalizedValue === 'no' || normalizedValue === '') {
      return false;
    }
  }
  return false;
}

function parseConfidenceScoreFromMapping(mapping: MapperMapping): number {
  const raw = mapping.confidence_score;
  if (raw === undefined || raw === null) {
    return 0;
  }
  if (typeof raw === 'string') {
    const parsed = parseFloat(raw.trim());
    if (!Number.isFinite(parsed)) return 0;
    return normalizeConfidenceNumeric(parsed);
  }
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return normalizeConfidenceNumeric(raw);
  }
  return 0;
}

function normalizeConfidenceNumeric(value: number): number {
  if (value > 0 && value <= 1) {
    return Math.round(value * 100);
  }
  if (value > 1 && value <= 100) {
    return Math.round(value);
  }
  if (value > 100) {
    return 100;
  }
  if (value === 0) {
    return 0;
  }
  return 0;
}

function getTransformForIndex(index: number): MappingRowItem['transform'] {
  return DEFAULT_TRANSFORMS[index] ?? 'rename';
}

function buildMappingRows(
  mappings: MapperMapping[] | undefined,
  sourceFields: MapperField[] | undefined,
  targetFields: MapperField[] | undefined
): MappingRowItem[] {
  const sourceFieldMap = new Map(
    (sourceFields ?? []).map((field) => [normalizeMappingValue(field.path).toLowerCase(), field])
  );
  const targetFieldMap = new Map(
    (targetFields ?? []).map((field) => [normalizeMappingValue(field.path).toLowerCase(), field])
  );

  return normalizeMappings(mappings).map((mapping, index) => {
    const sourceMatch = sourceFieldMap.get(mapping.source.toLowerCase());
    const targetMatch = targetFieldMap.get(mapping.destination.toLowerCase());
    const mappingStatus = String(mapping.status ?? '').toLowerCase();
    const isRejected =
      mappingStatus === 'rejected' || parseBooleanLike(mapping.rejected);
    const isAccepted =
      !isRejected &&
      (mappingStatus === 'approved' ||
        parseBooleanLike(mapping.approval) ||
        parseBooleanLike(mapping.approve) ||
        parseBooleanLike(mapping.approved));

    return {
      id: mapping.id,
      source: mapping.source,
      destination: mapping.destination,
      sourceType: normalizeFieldType(sourceMatch?.type),
      destinationType: normalizeFieldType(targetMatch?.type),
      confidence: parseConfidenceScoreFromMapping(mapping),
      transform: getTransformForIndex(index),
      accepted: isAccepted,
      rejected: isRejected,
      override: false,
      sourceOverridden: false,
      destinationOverridden: false,
    };
  });
}

function getWorkspaceGridClassName(
  isSourcePanelCollapsed: boolean,
  isTargetPanelCollapsed: boolean
) {
  if (isSourcePanelCollapsed && isTargetPanelCollapsed) {
    return 'lg:grid-cols-[56px_minmax(0,1fr)_56px] xl:grid-cols-[56px_minmax(0,1fr)_56px]';
  }
  if (isSourcePanelCollapsed) {
    return 'lg:grid-cols-[56px_minmax(0,1fr)_240px] xl:grid-cols-[56px_minmax(0,1fr)_260px]';
  }
  if (isTargetPanelCollapsed) {
    return 'lg:grid-cols-[240px_minmax(0,1fr)_56px] xl:grid-cols-[260px_minmax(0,1fr)_56px]';
  }
  return 'lg:grid-cols-[240px_minmax(0,1fr)_240px] xl:grid-cols-[260px_minmax(0,1fr)_260px]';
}

function getMappingRowClassName(row: MappingRowItem, isEditingRow: boolean) {
  let stateClassName = '';
  if (row.accepted) {
    stateClassName = 'bg-[#f5fdf8] dark:bg-success/10 border-l-[3px] border-l-[#00a86b] dark:border-l-success';
  } else if (row.rejected) {
    stateClassName = 'border-l-[3px] border-l-[#e53935] dark:border-l-danger bg-[#fff5f5] dark:bg-danger/10 opacity-50';
  } else if (isEditingRow) {
    stateClassName = 'border-l-[3px] border-l-[#00aeef] dark:border-l-primary bg-[#edf7ff] dark:bg-primary/10';
  } else if (row.override) {
    stateClassName = 'border-l-[3px] border-l-[#d4007f] dark:border-l-secondary';
  }

  return `grid grid-cols-[minmax(0,1fr)_minmax(72px,96px)_minmax(0,1fr)_96px] items-start gap-3 border-b border-[#e8eaec] dark:border-default-200 px-3 py-1 ${stateClassName}`;
}

function isUnmappedField(value: string) {
  return value.trim().toLowerCase() === '__unmapped__';
}

function getFieldTextClassName(
  value: string,
  isOverridden: boolean,
  defaultColorClassName: string
) {
  if (isUnmappedField(value) || !value) {
    return 'max-w-full truncate font-mono text-[12px] font-semibold text-[#e53935]';
  }
  if (isOverridden) {
    return 'max-w-full truncate font-mono text-[12px] font-semibold text-[#d4007f]';
  }
  return `max-w-full truncate font-mono text-[12px] font-semibold ${defaultColorClassName}`;
}

function getApprovalSuccessMessage(
  action: ApprovalAction,
  t: (key: string) => string
) {
  if (action === 'approve') {
    return t('Mapper approved successfully');
  }
  return t('Mapper rejected successfully');
}

function JsonUploadPanel({
  title,
  textValue,
  fileName,
  onTextChange,
  onFileSelect,
  panelType,
  isCollapsible = false,
  isCollapsed = false,
  onToggleCollapse,
  side = 'left',
}: Readonly<JsonUploadPanelProps>) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const collapseIcon = getCollapsePanelIcon(side, isCollapsed);

  const handleInputChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    await onFileSelect(file);
    event.target.value = '';
  };

  const handleDrop = async (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];
    if (!file) return;
    await onFileSelect(file);
  };

  if (isCollapsed) {
    return (
      <Card className={getPanelCardClassName()}>
        <div className="flex h-full min-h-0 flex-col">
          <div className="flex items-center justify-center bg-primary dark:bg-content2 px-2 py-[12px]">
            {isCollapsible ? (
              <button
                type="button"
                className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-background/10 text-white transition-colors hover:bg-background/20"
                onClick={onToggleCollapse}
                aria-label={t('Expand {{title}}', { title })}
              >
                <Icon icon={collapseIcon} width="18" />
              </button>
            ) : null}
          </div>
          <div className="flex flex-1 items-center justify-center px-1 py-4">
            <div className="flex flex-col items-center gap-3 text-primary dark:text-default-500">
              <Icon icon="material-symbols:view-sidebar-outline-rounded" width="18" />
              <div
                className="text-[10px] font-semibold uppercase tracking-[0.12em] text-primary dark:text-default-500"
                style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
              >
                {title}
              </div>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className={getPanelCardClassName()}>
      <div className="bg-primary dark:bg-content2 px-4 py-[12px]">
        <div className="flex items-center justify-between gap-2">
          <div className="text-xs font-semibold uppercase tracking-wide text-white">
            {title}
          </div>
          {isCollapsible ? (
            <button
              type="button"
              className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-background/10 text-white transition-colors hover:bg-background/20"
              onClick={onToggleCollapse}
              aria-label={t('Collapse {{title}}', { title })}
            >
              <Icon icon={collapseIcon} width="18" />
            </button>
          ) : null}
        </div>
      </div>
      <CardBody className="flex flex-1 min-h-0 flex-col gap-3 p-4">
        <div
          role="button"
          tabIndex={0}
          onClick={() => inputRef.current?.click()}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              inputRef.current?.click();
            }
          }}
          onDragOver={(event) => event.preventDefault()}
          onDrop={handleDrop}
          className="flex min-h-[150px] cursor-pointer flex-col items-center justify-center rounded-[14px] border border-dashed border-[#d0d5dd] bg-white px-4 text-center transition-colors hover:border-primary hover:bg-[#f7fbff] dark:border-default-200 dark:bg-content2 dark:hover:bg-content3"
        >
          <Icon icon="material-symbols:cloud-upload-outline-rounded" width="34" className="mb-2 text-[#0ea5e9]" />
          <div className="text-base font-semibold text-foreground">
            {t('Drop JSON file here')}
          </div>
          <div className="mt-1 text-sm leading-5 text-[#98a2b3] dark:text-default-500">
            {t('or click to browse .json up to 5MB')}
          </div>
          {fileName ? (
            <Chip size="sm" color="primary" variant="flat" className="mt-3 max-w-full">
              {fileName}
            </Chip>
          ) : null}
          <input
            ref={inputRef}
            type="file"
            accept="application/json,.json"
            className="hidden"
            onChange={handleInputChange}
          />
        </div>

        <div className="flex items-center justify-center py-1 text-[11px] font-medium uppercase tracking-wide text-[#98a2b3] dark:text-default-500">
          {t('or paste JSON below')}
        </div>

        <Textarea
          id={`${panelType}-schema-json`}
          minRows={7}
          maxRows={7}
          value={textValue}
          onValueChange={onTextChange}
          placeholder={SAMPLE_JSON_PLACEHOLDER}
          variant="bordered"
          aria-label={t('{{title}} JSON', { title })}
          classNames={{
            input: 'font-mono text-sm',
            inputWrapper: 'rounded-[12px] border border-[#d0d5dd] bg-white shadow-none dark:border-default-200 dark:bg-content2',
          }}
        />
      </CardBody>
    </Card>
  );
}

function FieldListPanel({
  title,
  subtitle,
  fields,
  searchValue,
  onSearchChange,
  isCollapsible = false,
  isCollapsed = false,
  onToggleCollapse,
  side = 'left',
}: Readonly<FieldListPanelProps>) {
  const { t } = useTranslation();
  const collapseIcon = getCollapsePanelIcon(side, isCollapsed);

  if (isCollapsed) {
    return (
      <Card className={getPanelCardClassName()}>
        <div className="flex h-full min-h-0 flex-col">
          <div className="flex items-center justify-center bg-primary dark:bg-content2 px-2 py-[12px]">
            {isCollapsible ? (
              <button
                type="button"
                className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-background/10 text-white transition-colors hover:bg-background/20"
                onClick={onToggleCollapse}
                aria-label={t('Expand {{title}}', { title })}
              >
                <Icon icon={collapseIcon} width="18" />
              </button>
            ) : null}
          </div>
          <div className="flex flex-1 items-center justify-center px-1 py-4">
            <div className="flex flex-col items-center gap-3 text-primary dark:text-default-500">
              <Icon icon="material-symbols:view-sidebar-outline-rounded" width="18" />
              <div
                className="text-[10px] font-semibold uppercase tracking-[0.12em] text-primary dark:text-default-500"
                style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
              >
                {title}
              </div>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className={getPanelCardClassName()}>
      <div className="bg-primary dark:bg-content2 px-4 py-[12px]">
        <div className="flex items-center justify-between gap-2">
          <div className="text-xs font-semibold uppercase tracking-wide text-white">
            {title}
          </div>
          <Chip size="sm" color="primary" variant="flat">
            {t('{{count}} fields', { count: fields.length })}
          </Chip>
          {isCollapsible ? (
            <button
              type="button"
              className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-background/10 text-white transition-colors hover:bg-background/20"
              onClick={onToggleCollapse}
              aria-label={t('Collapse {{title}}', { title })}
            >
              <Icon icon={collapseIcon} width="18" />
            </button>
          ) : null}
        </div>
      </div>
      <CardBody className="flex flex-1 min-h-0 flex-col gap-3 p-4">
        <div className="text-xs text-default-500">{subtitle}</div>
        <Input
          size="sm"
          value={searchValue}
          onValueChange={onSearchChange}
          placeholder={t('Search fields')}
          startContent={<Icon icon="material-symbols:search-rounded" width="16" className="text-default-400" />}
          aria-label={t('Search fields')}
          classNames={{
            inputWrapper: 'rounded-[10px] border border-[#d0d5dd] bg-white shadow-none dark:border-default-200 dark:bg-content2',
          }}
        />
        <Divider />
        <div className="flex-1 overflow-y-auto">
          {fields.length === 0 ? (
            <div className="flex h-full items-center justify-center text-sm text-default-500">
              {t('No fields found')}
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {fields.map((field) => (
                <div
                  key={field.path}
                  className="rounded-lg border border-default-200 bg-default-50 px-3 py-2"
                  style={{ marginLeft: `${Math.min((field.depth ?? 0) * 10, 36)}px` }}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-foreground">
                        {field.path}
                      </div>
                      <div className="mt-1 text-xs text-default-500">
                        {truncateText(formatSampleValue(field.sample_value))}
                      </div>
                    </div>
                    <Chip size="sm" variant="flat" color="primary">
                      {field.type ?? t('Unknown')}
                    </Chip>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardBody>
    </Card>
  );
}

interface MappingRowViewProps {
  row: MappingRowItem;
  index: number;
  editingState: EditingState | null;
  t: (key: string, options?: Record<string, unknown>) => string;
  startEdit: (row: MappingRowItem, field: EditingState['field']) => void;
  handleEditingValueChange: (value: string) => void;
  confirmEdit: () => void;
  cancelEdit: () => void;
  toggleAccept: (mappingId: string) => void;
  toggleReject: (mappingId: string) => void;
}

function MappingRowView({
  row,
  index,
  editingState,
  t,
  startEdit,
  handleEditingValueChange,
  confirmEdit,
  cancelEdit,
  toggleAccept,
  toggleReject,
}: Readonly<MappingRowViewProps>) {
  const isEditingRow = editingState?.rowId === row.id;
  const isEditingSource =
    isEditingRow && editingState.field === 'source';
  const isEditingDestination =
    isEditingRow && editingState.field === 'destination';

  return (
    <div
      key={row.id}
      className={getMappingRowClassName(row, isEditingRow)}
    >
      <div className="min-w-0 overflow-hidden">
        {isEditingSource ? (
          <div className="flex min-w-0 flex-col gap-2">
            <div className="relative">
              <Input
                aria-label={t('Source field {{index}}', {
                  index: index + 1,
                })}
                value={editingState.value}
                size="sm"
                variant="bordered"
                placeholder={t('Type to search source field')}
                onValueChange={handleEditingValueChange}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') confirmEdit();
                  if (event.key === 'Escape') cancelEdit();
                }}
                classNames={{
                  inputWrapper:
                    'rounded-[4px] border border-[#00aeef] bg-white shadow-none dark:bg-content2',
                }}
                autoFocus
              />
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="rounded-[4px] bg-[#00a86b] px-3 py-1 text-[11px] font-bold text-white"
                onClick={confirmEdit}
              >
                {t('Confirm')}
              </button>
              <button
                type="button"
                className="rounded-[4px] border border-[#d1d3d4] bg-white px-3 py-1 text-[11px] text-[#9aa3b2] dark:border-default-300 dark:bg-content2 dark:text-default-500"
                onClick={cancelEdit}
              >
                {t('Cancel')}
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            className="inline-flex max-w-full items-center gap-1 text-left"
            onClick={() => startEdit(row, 'source')}
          >
            <span
              className={getFieldTextClassName(
                row.source,
                Boolean(row.sourceOverridden),
                'text-[#1a1e2e] dark:text-foreground'
              )}
            >
              {row.source || t('Select source field')}
            </span>
            <Icon
              icon="material-symbols:edit-outline-rounded"
              width="13"
              className="shrink-0 text-[#00aeef]"
            />
          </button>
        )}
      </div>

      <div className="flex items-center justify-center self-center px-0.5">
        <div className="flex w-full max-w-[140px] items-center gap-0.5">
          <div className="h-px min-w-[6px] flex-1 border-t border-dashed border-warning" />
          <div
            className="shrink-0 rounded-full border border-dashed border-warning bg-warning/10 px-1.5 py-0.5 text-[10px] font-bold leading-none text-warning"
            title={t('Mapping confidence')}
          >
            {mappingConfidenceLabel(row.confidence)}
          </div>
          <div className="h-px min-w-[6px] flex-1 border-t border-dashed border-warning" />
        </div>
      </div>

      <div className="min-w-0 overflow-hidden">
        {isEditingDestination ? (
          <div className="flex min-w-0 flex-col gap-2">
            <div className="relative">
              <Input
                aria-label={t('Destination field {{index}}', {
                  index: index + 1,
                })}
                value={editingState.value}
                size="sm"
                variant="bordered"
                placeholder={t('Type to search destination')}
                onValueChange={handleEditingValueChange}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') confirmEdit();
                  if (event.key === 'Escape') cancelEdit();
                }}
                classNames={{
                  inputWrapper:
                    'rounded-[4px] border border-[#00aeef] bg-white shadow-none dark:bg-content2',
                }}
                autoFocus
              />
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="rounded-[4px] bg-[#00a86b] px-3 py-1 text-[11px] font-bold text-white"
                onClick={confirmEdit}
              >
                {t('Confirm')}
              </button>
              <button
                type="button"
                className="rounded-[4px] border border-[#d1d3d4] bg-white px-3 py-1 text-[11px] text-[#9aa3b2] dark:border-default-300 dark:bg-content2 dark:text-default-500"
                onClick={cancelEdit}
              >
                {t('Cancel')}
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            className="group inline-flex max-w-full items-center gap-1 text-left"
            onClick={() => startEdit(row, 'destination')}
          >
            <span
              className={getFieldTextClassName(
                row.destination,
                Boolean(row.destinationOverridden),
                'text-primary'
              )}
            >
              {row.destination || t('Select destination field')}
            </span>
            <Icon
              icon="material-symbols:edit-outline-rounded"
              width="13"
              className="shrink-0 text-[#00aeef] opacity-0 transition-opacity group-hover:opacity-100"
            />
          </button>
        )}
      </div>

      <div className="flex items-start justify-end gap-2 pt-1">
        {!isEditingRow ? (
          <>
            <button
              type="button"
              className={`inline-flex h-6 w-6 items-center justify-center rounded-[5px] border ${
                row.accepted
                  ? 'border-[#00a86b] bg-[#00a86b] text-white'
                  : 'border-[#c8e6c9] bg-[#e8f5e9] text-[#2e7d32] dark:border-success/40 dark:bg-success/15 dark:text-success'
              }`}
              onClick={() => toggleAccept(row.id)}
            >
              <Icon icon="material-symbols:check-rounded" width="14" />
            </button>
            <button
              type="button"
              className={`inline-flex h-6 w-6 items-center justify-center rounded-[5px] border ${
                row.rejected
                  ? 'border-[#e53935] bg-[#e53935] text-white'
                  : 'border-[#ffcdd2] bg-[#ffebee] text-[#c62828] dark:border-danger/40 dark:bg-danger/15 dark:text-danger'
              }`}
              onClick={() => toggleReject(row.id)}
            >
              <Icon icon="material-symbols:close-rounded" width="14" />
            </button>
          </>
        ) : null}
      </div>
    </div>
  );
}

export default function SchemaMapperMapper({
  mapper,
  onMapperChange,
  isMapperDataLoading = false,
}: Readonly<SchemaMapperMapperProps>) {
  const { t } = useTranslation();
  const [sourceJson, setSourceJson] = useState('');
  const [targetJson, setTargetJson] = useState('');
  const [sourceFileName, setSourceFileName] = useState('');
  const [targetFileName, setTargetFileName] = useState('');
  const [sourceSearch, setSourceSearch] = useState('');
  const [targetSearch, setTargetSearch] = useState('');
  const [userPromptAppend, setUserPromptAppend] = useState('');
  const userPromptAppendRef = useRef('');
  const [generatedData, setGeneratedData] = useState<GenerateMapperResponseData | null>(null);
  const [mappingRows, setMappingRows] = useState<MappingRowItem[]>([]);
  const [editingState, setEditingState] = useState<EditingState | null>(null);
  const [isSourcePanelCollapsed, setIsSourcePanelCollapsed] = useState(false);
  const [isTargetPanelCollapsed, setIsTargetPanelCollapsed] = useState(false);
  const [approvalAction, setApprovalAction] = useState<ApprovalAction>('approve');
  const [approvalComments, setApprovalComments] = useState('');
  const [isApprovalDialogOpen, setIsApprovalDialogOpen] = useState(false);
  const [isMappingsConfirmed, setIsMappingsConfirmed] = useState(false);
  const [baselineMapperDefinitionJson, setBaselineMapperDefinitionJson] = useState('');
  const [mapperDefinitionJson, setMapperDefinitionJson] = useState('');
  const isMapperDefinitionJsonSyncRef = useRef(false);
  const previousMapperIdRef = useRef<string | null>(null);
  const regeneratePromptContainerRef = useRef<HTMLDivElement | null>(null);
  const [isRegeneratePromptFocused, setIsRegeneratePromptFocused] = useState(false);
  const [regeneratePromptInputKey, setRegeneratePromptInputKey] = useState(0);

  const [generateMapper, { isLoading: isGenerating }] = useGenerateMapperMutation();
  const [updateMapper, { isLoading: isUpdatingMapper }] = useUpdateMapperMutation();
  const [approveMapper, { isLoading: isSubmittingApproval }] = useApproveMapperMutation();

  const handleUserPromptChange = (value: string) => {
    userPromptAppendRef.current = value;
    setUserPromptAppend(value);
  };

  const parsedSourceJson = useMemo(() => safeParseJson(sourceJson), [sourceJson]);
  const parsedTargetJson = useMemo(() => safeParseJson(targetJson), [targetJson]);

  const sourceFields = generatedData?.source_fields ?? [];
  const targetFields = generatedData?.target_fields ?? [];
  const sourceSampleForGenerate = useMemo(
    () => parsedSourceJson ?? buildSampleFromFields(sourceFields),
    [parsedSourceJson, sourceFields]
  );
  const targetSampleForGenerate = useMemo(
    () => parsedTargetJson ?? buildSampleFromFields(targetFields),
    [parsedTargetJson, targetFields]
  );
  const canGenerate =
    Boolean(mapper?.aiworker_id) &&
    Boolean(sourceSampleForGenerate) &&
    Boolean(targetSampleForGenerate);
  const sourceFieldMap = useMemo(
    () => new Map(sourceFields.map((field) => [field.path?.toLowerCase(), field])),
    [sourceFields]
  );
  const targetFieldMap = useMemo(
    () => new Map(targetFields.map((field) => [field.path?.toLowerCase(), field])),
    [targetFields]
  );

  const filteredSourceFields = useMemo(() => {
    const query = sourceSearch?.trim()?.toLowerCase();
    if (!query) return sourceFields;
    return sourceFields.filter((field) =>
      `${field.path} ${field.type ?? ''}`.toLowerCase()?.includes(query)
    );
  }, [sourceFields, sourceSearch]);

  const filteredTargetFields = useMemo(() => {
    const query = targetSearch.trim()?.toLowerCase();
    if (!query) return targetFields;
    return targetFields.filter((field) =>
      `${field.path} ${field.type ?? ''}`.toLowerCase()?.includes(query)
    );
  }, [targetFields, targetSearch]);

  const applyMapperDefinitionJsonSnapshots = useCallback(
    (
      definition: MapperDefinition | undefined,
      rows?: MappingRowItem[],
      options?: { baselineJson?: string }
    ) => {
      const modifiedJson = stringifyMapperDefinitionJson(definition, rows);
      isMapperDefinitionJsonSyncRef.current = true;
      setMapperDefinitionJson(modifiedJson);
      if (options?.baselineJson !== undefined) {
        setBaselineMapperDefinitionJson(options.baselineJson);
      }
      queueMicrotask(() => {
        isMapperDefinitionJsonSyncRef.current = false;
      });
    },
    []
  );

  const syncMapperData = (
    nextGeneratedData: GenerateMapperResponseData | null,
    jsonSnapshotOptions?: { baselineJson?: string }
  ) => {
    setGeneratedData(nextGeneratedData);
    const nextRows = buildMappingRows(
      nextGeneratedData?.mapper_definition?.mappings,
      nextGeneratedData?.source_fields,
      nextGeneratedData?.target_fields
    );
    setMappingRows(nextRows);

    if (nextGeneratedData?.mapper_definition) {
      applyMapperDefinitionJsonSnapshots(
        nextGeneratedData.mapper_definition,
        nextRows,
        jsonSnapshotOptions
      );
    }
  };

  const updateMapperSnapshot = (
    mapperUpdates: Partial<MapperItem> | undefined,
    nextGeneratedData: GenerateMapperResponseData | null
  ) => {
    if (!mapper && !mapperUpdates) return;
    const nextMapper: MapperItem = {
      ...(mapper ?? ({} as MapperItem)),
      ...omitUndefinedMapperItemFields(mapperUpdates),
      ...(nextGeneratedData?.mapper_definition
        ? { mapper_definition: nextGeneratedData.mapper_definition }
        : {}),
      ...(nextGeneratedData?.source_fields
        ? { source_fields: nextGeneratedData.source_fields }
        : {}),
      ...(nextGeneratedData?.target_fields
        ? { target_fields: nextGeneratedData.target_fields }
        : {}),
      ...(nextGeneratedData?.mapper_status
        ? { mapper_status: nextGeneratedData.mapper_status }
        : {}),
      ...(nextGeneratedData?.mapper_version
        ? { mapper_version: nextGeneratedData.mapper_version }
        : {}),
      // Preserve a valid mapper id even when API update response omits/empties it.
      aiworker_id:
        mapperUpdates?.aiworker_id || mapper?.aiworker_id || '',
    };
    onMapperChange?.(nextMapper);
  };

  useEffect(() => {
    const currentMapperId = mapper?.aiworker_id ?? null;
    const hasMapperChanged = previousMapperIdRef.current !== currentMapperId;
    previousMapperIdRef.current = currentMapperId;

    const mapperGeneratedData = getMapperGeneratedData(mapper);
    if (!mapperGeneratedData) {
      if (hasMapperChanged) {
        setIsMappingsConfirmed(false);
        setEditingState(null);
      }
      return;
    }

    const loadedDefinitionJson = stringifyMapperDefinitionJson(
      mapperGeneratedData.mapper_definition
    );
    syncMapperData(mapperGeneratedData, { baselineJson: loadedDefinitionJson });

    // Preserve "Confirm All" state while same mapper refreshes.
    if (hasMapperChanged) {
      setIsMappingsConfirmed(false);
      setEditingState(null);
      setIsSourcePanelCollapsed(true);
      setIsTargetPanelCollapsed(true);
    }
  }, [mapper]);

  const handleFileUpload = async (file: File, panelType: 'source' | 'target') => {
    if (!file.name?.toLowerCase()?.endsWith('.json')) {
      CtyShowToast(checkType.error, t('Please upload a JSON file'));
      return;
    }

    if (file.size > JSON_FILE_SIZE_LIMIT) {
      CtyShowToast(checkType.error, t('JSON file size should be 5MB or less'));
      return;
    }

    try {
      const fileContent = await readJsonFile(file);
      if (panelType === 'source') {
        setSourceJson(fileContent);
        setSourceFileName(file.name);
      } else {
        setTargetJson(fileContent);
        setTargetFileName(file.name);
      }
    } catch (error) {
      CtyShowToast(
        checkType.error,
        (error as Error)?.message ?? t('Failed to read selected file')
      );
    }
  };

  const startEdit = (
    row: MappingRowItem,
    field: EditingState['field']
  ) => {
    setEditingState({
      rowId: row.id,
      field,
      value: field === 'source' ? row.source : row.destination,
      originalValue: field === 'source' ? row.source : row.destination,
    });
  };

  const cancelEdit = () => {
    setEditingState(null);
  };

  const confirmEdit = () => {
    if (!editingState) return;

    const normalizedValue = editingState.value.trim();
    if (!normalizedValue) {
      CtyShowToast(checkType.error, t('Please enter a field name'));
      return;
    }

    const fieldMap =
      editingState.field === 'source' ? sourceFieldMap : targetFieldMap;
    const fieldMatch = fieldMap.get(normalizedValue?.toLowerCase());

    setMappingRows((currentRows) =>
      currentRows.map((row) => {
        if (row.id !== editingState.rowId) return row;

        const nextRow = {
          ...row,
          [editingState.field]: fieldMatch?.path ?? normalizedValue,
          override: row.override || normalizedValue !== editingState.originalValue,
          sourceOverridden:
            editingState.field === 'source'
              ? row.sourceOverridden || normalizedValue !== editingState.originalValue
              : row.sourceOverridden,
          destinationOverridden:
            editingState.field === 'destination'
              ? row.destinationOverridden || normalizedValue !== editingState.originalValue
              : row.destinationOverridden,
          accepted: false,
          rejected: false,
        };

        if (editingState.field === 'source') {
          nextRow.sourceType = normalizeFieldType(fieldMatch?.type ?? row.sourceType);
        } else {
          nextRow.destinationType = normalizeFieldType(
            fieldMatch?.type ?? row.destinationType
          );
        }

        return nextRow;
      })
    );

    setIsMappingsConfirmed(false);
    setEditingState(null);
  };

  const handleEditingValueChange = (value: string) => {
    setEditingState((currentState) =>
      currentState ? { ...currentState, value } : currentState
    );
  };

  const handleRegeneratePromptFocus = () => {
    setIsRegeneratePromptFocused(true);
  };

  const handleRegeneratePromptBlur = () => {
    requestAnimationFrame(() => {
      const container = regeneratePromptContainerRef.current;
      const activeElement = document.activeElement;
      if (!container?.contains(activeElement)) {
        setIsRegeneratePromptFocused(false);
      }
    });
  };

  const toggleAccept = (mappingId: string) => {
    setMappingRows((currentRows) =>
      currentRows.map((row) =>
        row.id === mappingId
          ? { ...row, accepted: !row.accepted, rejected: false }
          : row
      )
    );
    setIsMappingsConfirmed(false);
  };

  const toggleReject = (mappingId: string) => {
    setMappingRows((currentRows) =>
      currentRows.map((row) =>
        row.id === mappingId
          ? { ...row, rejected: !row.rejected, accepted: false }
          : row
      )
    );
    setIsMappingsConfirmed(false);
  };

  const handleConfirmAccepted = async () => {
    const mapperId = mapper?.aiworker_id;
    if (!mapperId) {
      CtyShowToast(checkType.error, t('Mapper ID not found'));
      return;
    }

    if (editingState) {
      CtyShowToast(checkType.error, t('Please confirm or cancel the current edit first'));
      return;
    }

    if (mappingRows.length === 0) {
      CtyShowToast(checkType.error, t('No mappings available to confirm'));
      return;
    }

    // Confirm All should only auto-approve rows that are still pending.
    const acceptedRows = mappingRows.map((row) => {
      if (row.accepted || row.rejected) {
        return row;
      }

      return {
        ...row,
        accepted: true,
        rejected: false,
      };
    });

    setMappingRows(acceptedRows);

    const nextMapperDefinition = buildUpdatedMapperDefinition(
      generatedData?.mapper_definition ?? mapper?.mapper_definition,
      acceptedRows
    );

    try {
      const response = await updateMapper({
        aiworker_id: mapperId,
        mapper_definition: nextMapperDefinition,
      }).unwrap();

      const normalizedResponseData = getNormalizedGeneratedData(response?.data, generatedData);
      const responseMapperDefinition = mergePersistedMapperDefinition(
        normalizedResponseData?.mapper_definition,
        nextMapperDefinition
      );
      const nextGeneratedData: GenerateMapperResponseData = {
        ...(normalizedResponseData ?? {}),
        mapper_definition: responseMapperDefinition,
        mapper_status:
          response?.data?.mapper_status ??
          normalizedResponseData?.mapper_status ??
          generatedData?.mapper_status ??
          responseMapperDefinition.status,
        mapper_version:
          response?.data?.mapper_version ??
          normalizedResponseData?.mapper_version ??
          generatedData?.mapper_version ??
          responseMapperDefinition.version,
      };
      syncMapperData(nextGeneratedData, {
        baselineJson: stringifyMapperDefinitionJson(responseMapperDefinition),
      });
      updateMapperSnapshot(response?.data, nextGeneratedData);
      setIsMappingsConfirmed(true);

      CtyShowToast(
        checkType.success, t('Mapper updated successfully')
      );
    } catch (error) {
      CtyShowToast(
        checkType.error,
        (error as Error)?.message ?? t('Failed to update mapper')
      );
    }
  };

  const openApprovalDialog = async (action: ApprovalAction) => {
    setApprovalAction(action);
    setApprovalComments('');
    if (document.fullscreenElement) {
      try {
        await document.exitFullscreen();
      } catch {
        // Ignore fullscreen exit failure and continue opening dialog.
      }
    }
    setIsApprovalDialogOpen(true);
  };

  const handleCloseApprovalDialog = () => {
    if (isSubmittingApproval) return;
    setIsApprovalDialogOpen(false);
    setApprovalComments('');
  };

  const handleSubmitApprovalAction = async () => {
    const mapperId = mapper?.aiworker_id;
    if (!mapperId) {
      CtyShowToast(checkType.error, t('Mapper ID not found'));
      return;
    }

    if (!approvalComments.trim()) {
      CtyShowToast(
        checkType.error,
        t('Please enter comments before submitting {{action}}', {
          action: approvalAction,
        })
      );
      return;
    }

    try {
      const response = await approveMapper({
        aiworker_id: mapperId,
        action: approvalAction,
        comments: approvalComments.trim(),
      }).unwrap();

      const nextGeneratedData = getNormalizedGeneratedData(response?.data, generatedData);
      syncMapperData(nextGeneratedData);
      updateMapperSnapshot(response?.data, nextGeneratedData);
      setIsApprovalDialogOpen(false);
      setApprovalComments('');
      setIsMappingsConfirmed(false);
      CtyShowToast(
        checkType.success,
        getApprovalSuccessMessage(approvalAction, t)
      );
    } catch (error) {
      CtyShowToast(
        checkType.error,
        (error as Error)?.message ??
          t('Failed to {{action}} mapper', {
            action: approvalAction,
          })
      );
    }
  };

  const handleGenerate = async () => {
    const mapperId = mapper?.aiworker_id;
    if (!mapperId) {
      CtyShowToast(checkType.error, t('Mapper ID not found'));
      return;
    }

    if (!sourceSampleForGenerate) {
      CtyShowToast(checkType.error, t('Please enter valid Source JSON'));
      return;
    }

    if (!targetSampleForGenerate) {
      CtyShowToast(checkType.error, t('Please enter valid Destination JSON'));
      return;
    }

    try {
      const response = await generateMapper({
        aiworker_id: mapperId,
        source_sample: sourceSampleForGenerate,
        target_sample: targetSampleForGenerate,
        integration_id: 'mapper',
        aligned_examples: false,
        user_prompt_append: userPromptAppendRef.current.trim(),
      }).unwrap();

      if (!response?.data) {
        CtyShowToast(checkType.error, t('Mapper response is empty'));
        return;
      }

      const previousJson = generatedData
        ? stringifyMapperDefinitionJson(
            generatedData.mapper_definition,
            mappingRows
          )
        : stringifyMapperDefinitionJson(response.data.mapper_definition);

      syncMapperData(response.data, {
        baselineJson: previousJson,
      });
      updateMapperSnapshot(undefined, response.data);
      setIsMappingsConfirmed(false);
      setEditingState(null);
      setIsSourcePanelCollapsed(true);
      setIsTargetPanelCollapsed(true);
      userPromptAppendRef.current = '';
      setUserPromptAppend('');
      setRegeneratePromptInputKey((currentValue) => currentValue + 1);
      CtyShowToast(
        checkType.success,
        t('Mapper generated successfully')
      );
    } catch (error) {
      CtyShowToast(
        checkType.error,
        (error as Error)?.message ?? t('Failed to generate mapper')
      );
    }
  };

  const acceptedCount = mappingRows.filter((row) => row.accepted).length;
  const rejectedCount = mappingRows.filter((row) => row.rejected).length;
  const pendingCount = mappingRows.filter(
    (row) => !row.accepted && !row.rejected
  ).length;
  const manualOverrideCount = mappingRows.filter((row) => row.override).length;

  const fieldMappingsHeaderStats = useMemo(() => {
    const md = generatedData?.mapper_definition;
    return {
      totalMappings: Math.round(numberFromApi(md?.total_mappings)),
      defaultMapped: Math.round(numberFromApi(md?.default_mapped_count)),
      defaultUnmapped: Math.round(numberFromApi(md?.default_unmapped_count)),
      mappedPercentageLabel: formatPercentageForDisplay(
        md?.mapped_percentage ?? md?.default_mapped_percentage
      ),
    };
  }, [generatedData?.mapper_definition]);

  const handleMapperDefinitionJsonChange = (value: string) => {
    setMapperDefinitionJson(value);
    if (isMapperDefinitionJsonSyncRef.current || !generatedData) return;

    try {
      const parsed = JSON.parse(value) as MapperDefinition;
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return;

      setGeneratedData({
        ...generatedData,
        mapper_definition: parsed,
      });
      setMappingRows(
        buildMappingRows(
          parsed.mappings,
          generatedData.source_fields,
          generatedData.target_fields
        )
      );
    } catch {
      // Monaco validation surfaces syntax errors inline.
    }
  };

  const canReviewMapper = mappingRows.length > 0 && isMappingsConfirmed;
  const hasPersistedMappings = Boolean(mapper?.mapper_definition?.mappings?.length);
  const shouldShowMapperHydrationLoader =
    isMapperDataLoading || (hasPersistedMappings && !generatedData);
  const workspaceGridClassName = getWorkspaceGridClassName(
    isSourcePanelCollapsed,
    isTargetPanelCollapsed
  );

  let fieldMappingsCardBody: ReactNode;
  if (generatedData) {
    fieldMappingsCardBody = (
      <div className="grid h-full min-h-0 grid-rows-[minmax(0,1fr)_auto]">
        <div className="relative min-h-0 overflow-hidden">
          <JsonMapperMonacoEditor
            original={baselineMapperDefinitionJson}
            value={mapperDefinitionJson}
            onChange={handleMapperDefinitionJsonChange}
            ariaLabel={t('Mapper JSON configuration')}
          />
        </div>

        <div className="relative z-10 border-t border-[#e8eaec] bg-[#fafbfc] px-3 py-3 text-[11px] text-[#5a6478] dark:border-default-200 dark:bg-content2 dark:text-default-500">
          <div className="flex items-center gap-3">
            <div className="flex min-w-0 flex-1 items-center gap-3">
              {!isRegeneratePromptFocused ? (
                <div className="shrink-0 whitespace-nowrap">
                  <strong className="text-primary">{acceptedCount}</strong> {t('accepted')}
                  {' · '}
                  <strong className="text-primary">{rejectedCount}</strong> {t('rejected')}
                  {' · '}
                  <strong className="text-primary">{pendingCount}</strong> {t('pending')}
                  {' · '}
                  <span className="font-bold text-[#d4007f]">
                    {manualOverrideCount} {t('manual overrides')}
                  </span>
                </div>
              ) : null}
              <div
                ref={regeneratePromptContainerRef}
                className="min-w-0 flex-1 overflow-hidden rounded-medium border bg-white dark:bg-content2 chat-converse-input transition-all duration-200"
                onFocusCapture={handleRegeneratePromptFocus}
                onBlurCapture={handleRegeneratePromptBlur}
              >
                <CtyPromptInput
                  key={regeneratePromptInputKey}
                  id="schema-mapper-regenerate-prompt"
                  elementType={promptInputType.INPUT}
                  radius="lg"
                  handleSendClick={handleUserPromptChange}
                  placeholder={t('Add instructions to guide mapping generation')}
                  triggerEveryInputChange
                  clearInputOnSend={false}
                  canSendEmptyMessage
                  prefillText={userPromptAppend}
                  classNames={{
                    inputWrapper: `!bg-transparent shadow-none !rounded-medium transition-all duration-200 ${
                      isRegeneratePromptFocused ? '!min-h-[76px]' : '!min-h-[36px]'
                    }`,
                    innerWrapper: 'relative',
                    input: isRegeneratePromptFocused
                      ? 'pt-2 pb-2 pl-2 !pr-2 text-sm min-h-[56px]'
                      : 'py-1 pl-2 !pr-2 text-sm',
                  }}
                />
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-3">
              <CtyButton
                size="sm"
                color="primary"
                variant="solid"
                onPress={handleGenerate}
                isLoading={isGenerating}
                isDisabled={!canGenerate || isGenerating}
              >
                {t('Re Generate')}
              </CtyButton>
              <CtyButton
                size="sm"
                color="primary"
                variant="solid"
                onPress={handleConfirmAccepted}
                isLoading={isUpdatingMapper}
                isDisabled={isGenerating || isUpdatingMapper || mappingRows.length === 0}
              >
                {t('Confirm All')}
              </CtyButton>
              {canReviewMapper ? (
                <CtyButton
                  size="sm"
                  color="secondary"
                  variant="solid"
                  onPress={() => {
                    void openApprovalDialog('approve');
                  }}
                  isDisabled={isSubmittingApproval}
                >
                  {t('Approve/Reject')}
                </CtyButton>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    );
  } else if (shouldShowMapperHydrationLoader) {
    fieldMappingsCardBody = (
      <div className="flex h-full min-h-0 flex-col items-center justify-center gap-3 rounded-[14px] bg-[#fbfcfd] dark:bg-content2 text-center">
        <CtySpinner size="md" />
        <div className="text-sm text-default-500">
          {t('Loading mapper data...')}
        </div>
      </div>
    );
  } else {
    fieldMappingsCardBody = (
      <div className="flex h-full min-h-0 flex-col overflow-y-auto rounded-[14px] bg-[#fbfcfd] dark:bg-content2 px-2 text-center">
        <div className="my-auto flex flex-col items-center">
          <div className="text-[28px] font-semibold leading-[44px] text-[#101828] dark:text-foreground">
            {t('Get Started')}
          </div>
          <div className="mt-2 max-w-md text-base leading-6 text-[#667085] dark:text-default-500">
            {t('Complete the steps below to configure your mapping.')}
          </div>

          <div className="mt-6 flex max-w-[360px] flex-col gap-3 text-left">
            <div className="flex items-center gap-3">
              <div
                className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${
                  parsedSourceJson
                    ? 'bg-[#12b76a] text-white'
                    : 'bg-[#f2f4f7] text-[#344054] dark:bg-default-200 dark:text-default-600'
                }`}
              >
                {parsedSourceJson ? (
                  <Icon icon="material-symbols:check-rounded" width="14" />
                ) : (
                  '1'
                )}
              </div>
              <span className="text-sm font-medium text-[#667085] dark:text-default-500">
                {t('Select a source schema')}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <div
                className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${
                  parsedTargetJson
                    ? 'bg-[#12b76a] text-white'
                    : 'bg-[#f2f4f7] text-[#344054] dark:bg-default-200 dark:text-default-600'
                }`}
              >
                {parsedTargetJson ? (
                  <Icon icon="material-symbols:check-rounded" width="14" />
                ) : (
                  '2'
                )}
              </div>
              <span className="text-sm font-medium text-[#667085] dark:text-default-500">
                {t('Select a destination schema')}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex h-6 w-6 items-center justify-center rounded-full bg-[#f2f4f7] dark:bg-default-200 text-xs font-semibold text-[#98a2b3] dark:text-default-500">
                3
              </div>
              <span className="text-sm text-[#667085] dark:text-default-500">
                {t('Generate mappings and review mapper_definition.mappings')}
              </span>
            </div>
          </div>

          <div className="mt-8 w-full max-w-[760px]">
            <div className="flex items-end gap-3">
              <div className="flex-1 rounded-medium border bg-white dark:bg-content2 chat-converse-input min-h-[76px] overflow-hidden">
                <CtyPromptInput
                  id="schema-mapper-user-prompt"
                  elementType={promptInputType.TEXTAREA}
                  radius="lg"
                  handleSendClick={handleUserPromptChange}
                  placeholder={t('Add instructions to guide mapping generation')}
                  triggerEveryInputChange
                  clearInputOnSend={false}
                  canSendEmptyMessage
                  prefillText={userPromptAppend}
                  classNames={{
                    inputWrapper: `!bg-transparent shadow-none !rounded-medium`,
                    innerWrapper: "relative",
                    input: "pt-2 pb-2 pl-2 !pr-2 text-sm min-h-[56px]",
                  }}
                />
              </div>
              <CtyButton
                color='primary'
                variant="solid"
                onPress={handleGenerate}
                isLoading={isGenerating}
                isDisabled={!canGenerate}
              >
                {t('Generate')}
              </CtyButton>
            </div>
          </div>
          {!parsedSourceJson && sourceJson.trim() ? (
            <div className="mt-3 text-xs text-danger">
              {t('Source JSON must be a valid JSON object')}
            </div>
          ) : null}
          {!parsedTargetJson && targetJson.trim() ? (
            <div className="mt-1 text-xs text-danger">
              {t('Destination JSON must be a valid JSON object')}
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 overflow-hidden px-5 pt-2 pb-3">
      {!mapper?.aiworker_id ? (
        <Card className="flex-1 border border-warning bg-warning/10 shadow-none">
          <CardBody className="flex items-center justify-center text-center">
            <div className="max-w-md">
              <div className="text-lg font-semibold text-warning">
                {t('Save the mapper first')}
              </div>
              <div className="mt-2 text-sm text-warning">
                {t('You can open the Mapper tab after the configuration is saved.')}
              </div>
            </div>
          </CardBody>
        </Card>
      ) : (
        <div className={`grid h-full min-h-0 grid-cols-1 gap-4 ${workspaceGridClassName}`}>
          {generatedData ? (
            <FieldListPanel
              title={t('Source Schema')}
              subtitle={t('Generated from the uploaded source JSON')}
              fields={filteredSourceFields}
              searchValue={sourceSearch}
              onSearchChange={setSourceSearch}
              isCollapsible
              isCollapsed={isSourcePanelCollapsed}
              onToggleCollapse={() =>
                setIsSourcePanelCollapsed((currentValue) => !currentValue)
              }
              side="left"
            />
          ) : (
            <JsonUploadPanel
              title={t('Source Schema')}
              textValue={sourceJson}
              fileName={sourceFileName}
              onTextChange={setSourceJson}
              onFileSelect={(file) => handleFileUpload(file, 'source')}
              panelType="source"
              isCollapsible
              isCollapsed={isSourcePanelCollapsed}
              onToggleCollapse={() =>
                setIsSourcePanelCollapsed((currentValue) => !currentValue)
              }
              side="left"
            />
          )}

          <div className="flex h-full min-h-0 min-w-0 flex-col">
            <Card className={`${getPanelCardClassName()} min-h-0 flex-1`}>
              <div className="bg-primary dark:bg-content2 px-4 py-[12px]">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
                  <div className="text-sm font-semibold text-white">
                    {t('Field Mappings')}
                  </div>
                  <div className="flex flex-wrap items-center justify-end gap-2">
                    {generatedData ? (
                      <>
                        <span className="rounded-full bg-background/15 px-2.5 py-1 text-[11px] font-medium leading-tight text-white">
                          {t('{{n}} total mappings', {
                            n: fieldMappingsHeaderStats.totalMappings,
                          })}
                        </span>
                        <span className="rounded-full bg-background/15 px-2.5 py-1 text-[11px] font-medium leading-tight text-white">
                          {t('{{n}} default mapped', {
                            n: fieldMappingsHeaderStats.defaultMapped,
                          })}
                        </span>
                        <span className="rounded-full bg-background/15 px-2.5 py-1 text-[11px] font-medium leading-tight text-white">
                          {t('{{n}} default unmapped', {
                            n: fieldMappingsHeaderStats.defaultUnmapped,
                          })}
                        </span>
                        <span className="text-[11px] font-medium text-white/90">
                          {t('Avg {{pct}}% confidence', {
                            pct: fieldMappingsHeaderStats.mappedPercentageLabel,
                          })}
                        </span>
                      </>
                    ) : null}
                  </div>
                </div>
              </div>
              <CardBody className="flex min-h-0 flex-1 flex-col overflow-hidden p-4">
                {fieldMappingsCardBody}
              </CardBody>
            </Card>
          </div>

          {generatedData ? (
            <FieldListPanel
              title={t('Destination Schema')}
              subtitle={t('Generated from the uploaded destination JSON')}
              fields={filteredTargetFields}
              searchValue={targetSearch}
              onSearchChange={setTargetSearch}
              isCollapsible
              isCollapsed={isTargetPanelCollapsed}
              onToggleCollapse={() =>
                setIsTargetPanelCollapsed((currentValue) => !currentValue)
              }
              side="right"
            />
          ) : (
            <JsonUploadPanel
              title={t('Destination Schema')}
              textValue={targetJson}
              fileName={targetFileName}
              onTextChange={setTargetJson}
              onFileSelect={(file) => handleFileUpload(file, 'target')}
              panelType="target"
              isCollapsible
              isCollapsed={isTargetPanelCollapsed}
              onToggleCollapse={() =>
                setIsTargetPanelCollapsed((currentValue) => !currentValue)
              }
              side="right"
            />
          )}
        </div>
      )}
      <Modal
        isOpen={isApprovalDialogOpen}
        onOpenChange={(open) => {
          if (!open) handleCloseApprovalDialog();
        }}
        placement="center"
      >
        <ModalContent>
          <ModalHeader>
            {approvalAction === 'approve'
              ? t('Approve Mapper')
              : t('Reject Mapper')}
          </ModalHeader>
          <ModalBody>
            <div className="text-sm text-default-600">
              {t('Please select an action and add your comments.')}
            </div>
            <Select
              label={t('Action')}
              aria-label={t('Approval action')}
              selectedKeys={[approvalAction]}
              onChange={(event) =>
                setApprovalAction(event.target.value as ApprovalAction)
              }
              classNames={{
                trigger:
                  'rounded-[10px] border border-[#d0d5dd] bg-white shadow-none dark:border-default-200 dark:bg-content2',
              }}
            >
              <SelectItem key="approve" value="approve">
                {t('Approve')}
              </SelectItem>
              <SelectItem key="reject" value="reject">
                {t('Reject')}
              </SelectItem>
            </Select>
            <Textarea
              aria-label={
                approvalAction === 'approve'
                  ? t('Approval comments')
                  : t('Rejection comments')
              }
              minRows={4}
              value={approvalComments}
              onValueChange={setApprovalComments}
              placeholder={
                approvalAction === 'approve'
                  ? t('Add approval comments')
                  : t('Add rejection comments')
              }
              classNames={{
                inputWrapper:
                  'rounded-[10px] border border-[#d0d5dd] bg-white shadow-none dark:border-default-200 dark:bg-content2',
              }}
            />
          </ModalBody>
          <ModalFooter>
            <CtyButton
              variant="bordered"
              color="default"
              onPress={handleCloseApprovalDialog}
              isDisabled={isSubmittingApproval}
            >
              {t('Cancel')}
            </CtyButton>
            <CtyButton
              color={approvalAction === 'approve' ? 'primary' : 'danger'}
              variant="solid"
              onPress={handleSubmitApprovalAction}
              isLoading={isSubmittingApproval}
            >
              {approvalAction === 'approve' ? t('Approve') : t('Reject')}
            </CtyButton>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
}

export { SchemaMapperMapper };
